/**
 * useLectureListScreen.js
 * -----------------------
 * State management hook for Education > Lectures > List of Lectures submodule.
 * Complies with module_implementation_standards.md:
 * - Rule 88: Zero native browser dialogs (appConfirm & appToast only).
 * - Rule 89: Background JobEngine for Excel/PDF export (useExportJob).
 * - Enterprise Print Engine: executePrintReport with 1,000-row guardrail.
 * - Dynamic options from shared master APIs (getMasterOptionItems, acheadFilterApi).
 */
import { useEffect, useMemo, useState, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  listLectures,
  deleteLecture,
  toggleCancelLecture,
  fetchReportData as apiFetchReportData,
} from '../api/lectureApi'
import { getMasterOptionItems } from '../../../admission/students/api/mastersApi'
import api from '../../../../api/client'
import { appToast } from '../../../../components/AppToast'
import { appConfirm } from '../../../../components/AppConfirmModal'
import { useExportJob } from '../../../../hooks/useExportJob'
import { executePrintReport } from '../../../../utils/printReportUtil'
import { LECTURE_PREDEFINED_REPORTS } from '../../../../components/PredefinedReportsDrawer'

export const SEARCH_FIELD_OPTIONS = [
  { value: 'Course', label: 'Course' },
  { value: 'Faculty', label: 'Faculty' },
  { value: 'Subject', label: 'Subject' },
  { value: 'Topic', label: 'Topic' },
  { value: 'Branch', label: 'Branch' },
  { value: 'Batch', label: 'Batch' },
]

export const PERIOD_OPTIONS = [
  { value: 'Period', label: 'Period' },
  { value: 'Today', label: 'Today' },
  { value: 'Yesterday', label: 'Yesterday' },
  { value: 'Current Week', label: 'Current Week' },
  { value: 'Current Month', label: 'Current Month' },
  { value: 'Current Year', label: 'Current Year' },
  { value: 'Last Week', label: 'Last Week' },
  { value: 'Last Month', label: 'Last Month' },
  { value: 'Last Year', label: 'Last Year' },
  { value: 'Next Week', label: 'Next Week' },
  { value: 'Next Month', label: 'Next Month' },
  { value: 'Next Year', label: 'Next Year' },
]

export const CATEGORY_OPTIONS = [
  { value: 'All', label: 'All Categories' },
  { value: 'Cancelled', label: 'Cancelled' },
  { value: 'Not Cancelled', label: 'Not Cancelled' },
]

export function generateAcademicYears(startYear = 2020, endYear = 2030) {
  const years = []
  for (let y = endYear; y >= startYear; y--) {
    const nextY = String(y + 1).slice(-2)
    const val = `${y}-${nextY}`
    years.push({ value: val, label: val })
  }
  return years
}

export const ACADEMIC_YEAR_OPTIONS = [
  { value: 'All', label: 'All Academic Years' },
  ...generateAcademicYears(),
]

export const STATUS_TABS = [
  { key: 'All', label: 'All' },
  { key: 'Scheduled', label: 'Scheduled' },
  { key: 'Completed', label: 'Completed' },
  { key: 'Late', label: 'Late' },
  { key: 'Cancelled', label: 'Cancelled' },
]

export default function useLectureListScreen() {
  const navigate = useNavigate()

  // Data state
  const [items, setItems] = useState([])
  const [totalCount, setTotalCount] = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const [loading, setLoading] = useState(true)

  // Filter state (100% legacy parity with LecturesDAL.cs:GetList)
  const [searchField, setSearchField] = useState('Course')
  const [searchText, setSearchText] = useState('')
  const [statusFilter, setStatusFilter] = useState('All')
  const [companyFilter, setCompanyFilter] = useState('All')
  const [branchFilter, setBranchFilter] = useState('All')
  const [courseFilter, setCourseFilter] = useState('All')
  const [batchFilter, setBatchFilter] = useState('All')
  const [subjectFilter, setSubjectFilter] = useState('All')
  const [facultyFilter, setFacultyFilter] = useState('All')
  const [categoryFilter, setCategoryFilter] = useState('All')
  const [academicYearFilter, setAcademicYearFilter] = useState('All')
  const [periodFilter, setPeriodFilter] = useState('Period')
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [orderBy, setOrderBy] = useState(0)

  // Pagination state
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [isReportsOpen, setIsReportsOpen] = useState(false)

  // Dynamic Master Options
  const [courseOptions, setCourseOptions] = useState([{ value: 'All', label: 'All Courses' }])
  const [batchOptions, setBatchOptions] = useState([{ value: 'All', label: 'All Batches' }])
  const [subjectOptions, setSubjectOptions] = useState([{ value: 'All', label: 'All Subjects' }])
  const [branchOptions, setBranchOptions] = useState([{ value: 'All', label: 'All Branches' }])
  const [companyOptions, setCompanyOptions] = useState([{ value: 'All', label: 'All Companies' }])
  const [facultyOptions, setFacultyOptions] = useState([{ value: 'All', label: 'All Faculty' }])

  // Background Export Hook (Rule 89)
  const { triggerExport: triggerJobExport, isExporting } = useExportJob({
    module: 'lectures',
    jobType: 'EXPORT',
  })

  // Load dynamic dropdown options on mount
  useEffect(() => {
    async function loadMasterOptions() {
      try {
        const [cItems, bItems, sItems, brItems, compItems] = await Promise.all([
          getMasterOptionItems('courses'),
          getMasterOptionItems('batches'),
          getMasterOptionItems('subjects'),
          getMasterOptionItems('branches'),
          getMasterOptionItems('institutes'),
        ])

        if (Array.isArray(cItems) && cItems.length > 0) {
          setCourseOptions([{ value: 'All', label: 'All Courses' }, ...cItems.map((c) => ({ value: String(c.id), label: c.name }))])
        }
        if (Array.isArray(bItems) && bItems.length > 0) {
          setBatchOptions([{ value: 'All', label: 'All Batches' }, ...bItems.map((b) => ({ value: String(b.id), label: b.name }))])
        }
        if (Array.isArray(sItems) && sItems.length > 0) {
          setSubjectOptions([{ value: 'All', label: 'All Subjects' }, ...sItems.map((s) => ({ value: String(s.id), label: s.name }))])
        }
        if (Array.isArray(brItems) && brItems.length > 0) {
          setBranchOptions([{ value: 'All', label: 'All Branches' }, ...brItems.map((b) => ({ value: String(b.id), label: b.name }))])
        }
        if (Array.isArray(compItems) && compItems.length > 0) {
          setCompanyOptions([{ value: 'All', label: 'All Companies' }, ...compItems.map((c) => ({ value: String(c.id), label: c.name }))])
        }

        // Load faculty from /options/acheads or fallback
        const { data: facData } = await api.get('/options/acheads', { params: { page: 1, page_size: 100 } })
        const facItems = facData?.items || []
        if (Array.isArray(facItems) && facItems.length > 0) {
          setFacultyOptions([{ value: 'All', label: 'All Faculty' }, ...facItems.map((f) => ({ value: String(f.id || f.value), label: f.name || f.label }))])
        }
      } catch (err) {
        console.error('Error loading lecture filter options:', err)
      }
    }

    loadMasterOptions()
  }, [])

  // Period Preset Selector with auto-calculated date ranges
  const handlePeriodChange = useCallback((val) => {
    setPeriodFilter(val)
    const now = new Date()
    const formatDate = (d) => d.toISOString().slice(0, 10)

    if (val === 'Today') {
      const t = formatDate(now)
      setFromDate(t)
      setToDate(t)
    } else if (val === 'Yesterday') {
      const y = new Date(now)
      y.setDate(y.getDate() - 1)
      const yt = formatDate(y)
      setFromDate(yt)
      setToDate(yt)
    } else if (val === 'Current Week') {
      const start = new Date(now)
      const day = start.getDay() || 7
      start.setDate(start.getDate() - day + 1)
      const end = new Date(start)
      end.setDate(end.getDate() + 6)
      setFromDate(formatDate(start))
      setToDate(formatDate(end))
    } else if (val === 'Current Month') {
      const start = new Date(now.getFullYear(), now.getMonth(), 1)
      const end = new Date(now.getFullYear(), now.getMonth() + 1, 0)
      setFromDate(formatDate(start))
      setToDate(formatDate(end))
    } else if (val === 'Current Year') {
      const start = new Date(now.getFullYear(), 0, 1)
      const end = new Date(now.getFullYear(), 11, 31)
      setFromDate(formatDate(start))
      setToDate(formatDate(end))
    } else if (val === 'Last Month') {
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1)
      const end = new Date(now.getFullYear(), now.getMonth(), 0)
      setFromDate(formatDate(start))
      setToDate(formatDate(end))
    } else if (val === 'Last Week') {
      const start = new Date(now)
      const day = start.getDay() || 7
      start.setDate(start.getDate() - day - 6)
      const end = new Date(start)
      end.setDate(end.getDate() + 6)
      setFromDate(formatDate(start))
      setToDate(formatDate(end))
    } else if (val === 'Period') {
      setFromDate('')
      setToDate('')
    }
    setPage(1)
  }, [])

  // Load lectures data from backend API
  const loadData = useCallback(async () => {
    try {
      setLoading(true)
      const res = await listLectures({
        academicYear: academicYearFilter,
        companyId: companyFilter !== 'All' ? companyFilter : undefined,
        branchId: branchFilter !== 'All' ? branchFilter : undefined,
        courseId: courseFilter !== 'All' ? courseFilter : undefined,
        batchId: batchFilter !== 'All' ? batchFilter : undefined,
        subjectId: subjectFilter !== 'All' ? subjectFilter : undefined,
        facultyId: facultyFilter !== 'All' ? facultyFilter : undefined,
        fromDate,
        toDate,
        status: statusFilter,
        category: categoryFilter,
        searchField,
        searchText,
        orderBy,
        page,
        pageSize,
      })

      setItems(res?.items || [])
      setTotalCount(res?.total || 0)
      setTotalPages(res?.total_pages || 1)
    } catch (err) {
      console.error('Failed to load lectures:', err)
      appToast.error('Failed to load lectures from server')
    } finally {
      setLoading(false)
    }
  }, [
    academicYearFilter,
    companyFilter,
    branchFilter,
    courseFilter,
    batchFilter,
    subjectFilter,
    facultyFilter,
    fromDate,
    toDate,
    statusFilter,
    categoryFilter,
    searchField,
    searchText,
    orderBy,
    page,
    pageSize,
  ])

  useEffect(() => {
    loadData()
  }, [loadData])

  // Reset Filters
  const handleReset = useCallback(() => {
    setSearchField('Course')
    setSearchText('')
    setStatusFilter('All')
    setCompanyFilter('All')
    setBranchFilter('All')
    setCourseFilter('All')
    setBatchFilter('All')
    setSubjectFilter('All')
    setFacultyFilter('All')
    setCategoryFilter('All')
    setAcademicYearFilter('All')
    setPeriodFilter('Period')
    setFromDate('')
    setToDate('')
    setOrderBy(0)
    setPage(1)
  }, [])

  // Segmented Status Tab Click
  const handleStatusTabChange = useCallback((tabKey) => {
    setStatusFilter(tabKey)
    setPage(1)
  }, [])

  // Row Action: Cancel or Restore Lecture (Rule 88: appConfirm)
  const handleToggleCancel = useCallback(
    async (item) => {
      const isCurrentlyCancelled = item.is_cancelled || item.status === 'Cancelled'
      const confirmed = await appConfirm({
        title: isCurrentlyCancelled ? 'Restore Lecture' : 'Cancel Lecture',
        message: `Are you sure you want to ${isCurrentlyCancelled ? 'restore' : 'cancel'} the lecture for "${item.course_name || 'Course'}" (${item.subject_name || 'Subject'})?`,
        confirmText: isCurrentlyCancelled ? 'Restore Lecture' : 'Cancel Lecture',
        cancelText: 'Back',
        variant: isCurrentlyCancelled ? 'primary' : 'warning',
      })
      if (!confirmed) return

      try {
        const res = await toggleCancelLecture(item.id)
        appToast.success(res?.message || 'Lecture updated successfully')
        loadData()
      } catch (err) {
        console.error('Failed to toggle lecture cancel status:', err)
        appToast.error(err?.response?.data?.detail?.message || 'Failed to update lecture status')
      }
    },
    [loadData]
  )

  // Row Action: Soft Delete Lecture (Rule 88: appConfirm)
  const handleDelete = useCallback(
    async (item) => {
      const confirmed = await appConfirm({
        title: 'Delete Lecture Record',
        message: `Are you sure you want to permanently delete the lecture on "${item.p_date || ''} - ${item.subject_name || ''}"? This action cannot be undone.`,
        confirmText: 'Delete',
        cancelText: 'Cancel',
        variant: 'danger',
      })
      if (!confirmed) return

      try {
        const res = await deleteLecture(item.id)
        appToast.success(res?.message || 'Lecture deleted successfully')
        loadData()
      } catch (err) {
        console.error('Failed to delete lecture:', err)
        appToast.error(err?.response?.data?.detail?.message || 'Failed to delete lecture')
      }
    },
    [loadData]
  )

  // Row Action: Attendance Navigation
  const handleAttendance = useCallback((item) => {
    navigate(`/lectures/attendance?id=${item.id}`)
  }, [navigate])

  // Row Action: Send SMS Alert
  const handleSendSMS = useCallback((item) => {
    appToast.success(`Schedule SMS alert queued for lecture #${item.id} (${item.faculty_name || 'Faculty'})`)
  }, [])

  // Row Action: Send WhatsApp Alert
  const handleSendWhatsApp = useCallback((item) => {
    appToast.success(`Schedule WhatsApp message queued for lecture #${item.id}`)
  }, [])

  // Tools: Instant Print Report (printReportUtil with 1,000-row guardrail)
  const handlePrintReport = useCallback(async () => {
    const currentFilters = {
      academicYear: academicYearFilter,
      companyId: companyFilter !== 'All' ? companyFilter : undefined,
      branchId: branchFilter !== 'All' ? branchFilter : undefined,
      courseId: courseFilter !== 'All' ? courseFilter : undefined,
      batchId: batchFilter !== 'All' ? batchFilter : undefined,
      subjectId: subjectFilter !== 'All' ? subjectFilter : undefined,
      facultyId: facultyFilter !== 'All' ? facultyFilter : undefined,
      fromDate,
      toDate,
      status: statusFilter,
      category: categoryFilter,
      searchField,
      searchText,
    }

    await executePrintReport({
      title: 'Lectures Schedule Report',
      subtitle: `Academic Year: ${academicYearFilter === 'All' ? 'All Years' : academicYearFilter} | Status: ${statusFilter}`,
      metadata: {
        'Course': courseFilter === 'All' ? 'All Courses' : (courseOptions.find((c) => c.value === courseFilter)?.label || courseFilter),
        'Branch': branchFilter === 'All' ? 'All Branches' : (branchOptions.find((b) => b.value === branchFilter)?.label || branchFilter),
        'Date Period': periodFilter !== 'Period' ? periodFilter : (fromDate && toDate ? `${fromDate} to ${toDate}` : 'All Dates'),
      },
      columns: [
        { key: 'course_name', label: 'Course' },
        { key: 'faculty_name', label: 'Faculty' },
        { key: 'p_date', label: 'Date' },
        { key: 'time_slot', label: 'Time Slot', format: (val, row) => `${row.p_from || ''} - ${row.p_till || ''}` },
        { key: 'subject_name', label: 'Subject' },
        { key: 'chapter_topic', label: 'Chapter / Topic', format: (val, row) => (row.chapter_name ? `${row.chapter_no ? row.chapter_no + ' - ' : ''}${row.chapter_name}` : row.topic || '-') },
        { key: 'branch_names', label: 'Branches' },
        { key: 'batch_names', label: 'Batches' },
        { key: 'status', label: 'Status' },
        { key: 'attendance', label: 'Attendance', format: (val, row) => (row.total_count > 0 ? `${row.present_count}/${row.total_count}` : '-') },
      ],
      fetchReportData: async () => {
        const res = await listLectures({
          ...currentFilters,
          page: 1,
          pageSize: 1000,
        })
        return res?.items || []
      },
      onExportPdf: () => triggerJobExport(currentFilters, 'PDF'),
      onExportExcel: () => triggerJobExport(currentFilters, 'XLSX'),
    })
  }, [
    academicYearFilter,
    companyFilter,
    branchFilter,
    courseFilter,
    batchFilter,
    subjectFilter,
    facultyFilter,
    fromDate,
    toDate,
    statusFilter,
    categoryFilter,
    searchField,
    searchText,
    courseOptions,
    branchOptions,
    periodFilter,
    triggerJobExport,
  ])

  // Predefined Reports Drawer Action Handler (Print, Excel, PDF)
  const handleReportAction = useCallback(
    async (actionKey, reportKey) => {
      const currentFilters = {
        academicYear: academicYearFilter,
        companyId: companyFilter !== 'All' ? companyFilter : undefined,
        branchId: branchFilter !== 'All' ? branchFilter : undefined,
        courseId: courseFilter !== 'All' ? courseFilter : undefined,
        batchId: batchFilter !== 'All' ? batchFilter : undefined,
        subjectId: subjectFilter !== 'All' ? subjectFilter : undefined,
        facultyId: facultyFilter !== 'All' ? facultyFilter : undefined,
        fromDate,
        toDate,
        status: statusFilter,
        category: categoryFilter,
        searchField,
        searchText,
      }

      if (actionKey === 'print') {
        const reportDef = LECTURE_PREDEFINED_REPORTS.find((r) => r.reportKey === reportKey)
        const repTitle = reportDef?.title || 'Lecture Report'

        // Apply saved column preferences from ReportColumnsModal (localStorage)
        let savedCols = null
        const savedColsJson = localStorage.getItem(`report_cols_${reportKey}`)
        if (savedColsJson) {
          try {
            const parsed = JSON.parse(savedColsJson)
            savedCols = parsed.filter((c) => c.visible).map((c) => c.label || c.key)
          } catch {
            savedCols = null
          }
        }

        const activeCols = savedCols && savedCols.length > 0 ? savedCols : (reportDef?.defaultColumns || [])
        const printColumns = activeCols.map((col) => ({
          key: col,
          label: col,
        }))

        await executePrintReport({
          title: repTitle,
          subtitle: `Academic Year: ${academicYearFilter === 'All' ? 'All Years' : academicYearFilter} | Status: ${statusFilter}`,
          metadata: {
            'Report': repTitle,
            'Academic Year': academicYearFilter === 'All' ? 'All Years' : academicYearFilter,
            'Course': courseFilter === 'All' ? 'All Courses' : (courseOptions.find((c) => c.value === courseFilter)?.label || courseFilter),
            'Branch': branchFilter === 'All' ? 'All Branches' : (branchOptions.find((b) => b.value === branchFilter)?.label || branchFilter),
            'Date Period': periodFilter !== 'Period' ? periodFilter : (fromDate && toDate ? `${fromDate} to ${toDate}` : 'All Dates'),
          },
          columns: printColumns,
          fetchReportData: async () => {
            const res = await apiFetchReportData(reportKey, currentFilters)
            return res?.rows || res?.items || []
          },
          onExportPdf: () => triggerJobExport({ ...currentFilters, report_key: reportKey }, 'PDF'),
          onExportExcel: () => triggerJobExport({ ...currentFilters, report_key: reportKey }, 'XLSX'),
        })
      } else if (actionKey === 'excel') {
        triggerJobExport({ ...currentFilters, report_key: reportKey }, 'XLSX')
      } else if (actionKey === 'pdf') {
        triggerJobExport({ ...currentFilters, report_key: reportKey }, 'PDF')
      }
    },
    [
      academicYearFilter,
      companyFilter,
      branchFilter,
      courseFilter,
      batchFilter,
      subjectFilter,
      facultyFilter,
      fromDate,
      toDate,
      statusFilter,
      categoryFilter,
      searchField,
      searchText,
      periodFilter,
      courseOptions,
      branchOptions,
      triggerJobExport,
    ],
  )

  // Tools Menu Selector Handler
  const handleToolSelect = useCallback(
    (key) => {
      const currentFilters = {
        academicYear: academicYearFilter,
        companyId: companyFilter !== 'All' ? companyFilter : undefined,
        branchId: branchFilter !== 'All' ? branchFilter : undefined,
        courseId: courseFilter !== 'All' ? courseFilter : undefined,
        batchId: batchFilter !== 'All' ? batchFilter : undefined,
        subjectId: subjectFilter !== 'All' ? subjectFilter : undefined,
        facultyId: facultyFilter !== 'All' ? facultyFilter : undefined,
        fromDate,
        toDate,
        status: statusFilter,
        category: categoryFilter,
        searchField,
        searchText,
      }

      if (key === 'reports-drawer' || key === 'reports' || key === 'print-report' || key === 'print') {
        setIsReportsOpen(true)
      } else if (key === 'export_excel' || key === 'excel') {
        triggerJobExport(currentFilters, 'XLSX')
      } else if (key === 'export_pdf' || key === 'pdf') {
        triggerJobExport(currentFilters, 'PDF')
      } else if (key === 'combined-sms') {
        appToast.info('Combined SMS broadcast queued for selected lectures')
      } else if (key === 'combined-whatsapp') {
        appToast.info('Combined WhatsApp broadcast queued for selected lectures')
      } else if (key === 'create-entries') {
        navigate('/lectures/timetable')
      } else if (key === 'upload') {
        navigate('/lectures/upload')
      }
    },
    [
      academicYearFilter,
      companyFilter,
      branchFilter,
      courseFilter,
      batchFilter,
      subjectFilter,
      facultyFilter,
      fromDate,
      toDate,
      statusFilter,
      categoryFilter,
      searchField,
      searchText,
      handlePrintReport,
      triggerJobExport,
      navigate,
    ],
  )

  return {
    state: {
      items,
      totalCount,
      totalPages,
      page,
      pageSize,
      loading,
      isExporting,
      searchField,
      searchText,
      statusFilter,
      companyFilter,
      branchFilter,
      courseFilter,
      batchFilter,
      subjectFilter,
      facultyFilter,
      categoryFilter,
      academicYearFilter,
      periodFilter,
      fromDate,
      toDate,
      orderBy,
      isReportsOpen,
      options: {
        searchField: SEARCH_FIELD_OPTIONS,
        period: PERIOD_OPTIONS,
        category: CATEGORY_OPTIONS,
        academicYear: ACADEMIC_YEAR_OPTIONS,
        course: courseOptions,
        batch: batchOptions,
        subject: subjectOptions,
        branch: branchOptions,
        company: companyOptions,
        faculty: facultyOptions,
      },
    },
    actions: {
      setPage,
      setPageSize,
      setSearchField,
      setSearchText,
      setStatusFilter,
      setCompanyFilter,
      setBranchFilter,
      setCourseFilter,
      setBatchFilter,
      setSubjectFilter,
      setFacultyFilter,
      setCategoryFilter,
      setAcademicYearFilter,
      setFromDate: (d) => {
        setFromDate(d)
        setPeriodFilter('Custom')
        setPage(1)
      },
      setToDate: (d) => {
        setToDate(d)
        setPeriodFilter('Custom')
        setPage(1)
      },
      setPeriodFilter: handlePeriodChange,
      setOrderBy: (o) => {
        setOrderBy(o)
        setPage(1)
      },
      handleReset,
      handleStatusTabChange,
      handleToggleCancel,
      handleDelete,
      handleAttendance,
      handleSendSMS,
      handleSendWhatsApp,
      handleToolSelect,
      handlePrintReport,
      handleReportAction,
      setIsReportsOpen,
      loadData,
    },
  }
}
